import type { CSSProperties } from "react";
import { styled } from "@mui/material/styles";
import { Box, Typography, SxProps, Theme } from "@mui/material";
import { themeGet } from "@styled-system/theme-get";
import colors from "../../shared/constants/color";
import type { WiserThemeTokens } from "../../shared/theme/wiserTheme";
import {
  getDialogActionsSx,
  getDialogTitleSx,
  lightNavyButtonSx,
  withDarkModalSaveButton,
} from "../../shared/theme/wiserThemeHelpers";

// CandidateDetail specific styles
export const MainContainer = styled(Box)`
  min-height: 100vh;
  font-family: ${themeGet("fonts.interUi")};
`;

export const PageTitle = styled(Typography)`
  margin-bottom: 24px;
  font-weight: 600;
  font-family: ${themeGet("fonts.interUi")};
`;

// FORK SAPMASI (Dalga 24): upstream'de yalnizca `display: flex; gap: 24px`.
// Asagidaki dort bildirim StatusTab'in sabit-yerlesimli tablosunun yatay
// tasmasini engelliyor; `min-width: 0` olmadan flex cocuk kucultulemiyor ve
// tablo kabi tasiyor. Upstream'den yeniden kopyalanirsa GERI GELMELI.
export const ContentWrapper = styled(Box)`
  display: flex;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
`;

export const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  font-family: ${themeGet("fonts.interUi")};
`;

export const ErrorContainer = styled(Box)`
  text-align: center;
  margin-top: 32px;
  font-family: ${themeGet("fonts.interUi")};
`;

// İş Tecrübesi modalı tarih alanları için stil
export const workExperienceDateSx: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    height: "40px",
    width: "100%",
    borderRadius: "4px",
    backgroundColor: colors.white,
    "& fieldset": { borderColor: "rgba(0, 0, 0, 0.23)" },
    "&:hover fieldset": { borderColor: "rgba(0, 0, 0, 0.87)" },
    "&.Mui-focused fieldset": { borderColor: "#1976d2" },
  },
  "& .MuiInputBase-input": {
    padding: "10px 14px",
    "&::placeholder": {
      color: "transparent",
    },
  },
  "& .MuiInputLabel-root": {
    transform: "translate(14px, 10px) scale(1)",
    transformOrigin: "top left",
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75)",
    },
  },
};

const interFont = themeGet("fonts.interUi");

// ===== StatusTab — notlar hücresi (upstream 09df543 + 366a5d0) =====

export type StatusTabTheme = {
  tableBorder: string;
  tableSurface: string;
  tableHeadBg: string;
  tableHeaderBarBg: string;
  tableHeaderColor: string;
  tableCellColor: string;
  tableTitleColor: string;
  rowHoverBg: string;
  notesColor: string;
  rowBorder: string;
  menuButtonBg: string;
  menuButtonColor: string;
  menuButtonHoverBg: string;
  textPrimary: string;
};

export const getStatusTabTheme = (
  darkModeActive: boolean,
  t: WiserThemeTokens,
): StatusTabTheme => ({
  tableBorder: darkModeActive ? t.borderStrong : "#E5E7EB",
  tableSurface: darkModeActive ? t.surface : "#fff",
  tableHeadBg: darkModeActive ? t.surfaceMuted : "#F8F9FA",
  tableHeaderBarBg: darkModeActive ? t.surfaceMuted : "#F9FAFB",
  tableHeaderColor: darkModeActive ? "#FFFFFF" : "#6B778C",
  tableCellColor: darkModeActive ? t.textMuted : "#6B778C",
  tableTitleColor: darkModeActive ? t.textPrimary : "#253858",
  rowHoverBg: darkModeActive ? "rgba(255,255,255,0.05)" : "#F9FAFB",
  notesColor: darkModeActive ? t.textPrimary : "#172B4D",
  rowBorder: darkModeActive ? t.borderStrong : "#F3F4F6",
  menuButtonBg: darkModeActive ? t.inputBg : "#F4F5F7",
  menuButtonColor: darkModeActive ? t.textPrimary : "#42526E",
  menuButtonHoverBg: darkModeActive ? t.surfaceMuted : "#F4F5F7",
  textPrimary: t.textPrimary,
});

export const getStatusTabRowBg = (
  darkModeActive: boolean,
  t: WiserThemeTokens,
  index: number,
) =>
  darkModeActive
    ? index % 2 === 0
      ? t.surface
      : t.surfaceMuted
    : index % 2 === 0
      ? "#fff"
      : "#FAFAFA";

export const getInterviewResultColor = (status: string) => {
  if (status.toLowerCase().includes("olumlu")) {
    return "#E3FCEF";
  }
  if (
    status.toLowerCase().includes("olumsuz") ||
    status.toLowerCase().includes("red")
  ) {
    return "#FFEBE6";
  }
  return "#F4F5F7";
};

export const getKvkkStatusBg = (status: string) => {
  switch (status) {
    case "Onaylandı":
      return "#E3FCEF";
    case "Reddedildi":
      return "#FFEBE6";
    case "Gönderildi":
      return "#FFFAE6";
    case "Gönderilmedi":
      return "#F4F5F7";
    default:
      return "#F4F5F7";
  }
};

export const getStatusTabLoadingSx = (st: StatusTabTheme): SxProps<Theme> => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: 400,
  backgroundColor: st.tableSurface,
  borderRadius: 2,
  border: `1px solid ${st.tableBorder}`,
});

export const getStatusTabRootSx = (st: StatusTabTheme): SxProps<Theme> => ({
  backgroundColor: st.tableSurface,
  borderRadius: 2,
  border: `1px solid ${st.tableBorder}`,
  fontFamily: interFont,
  minWidth: "1200px",
  overflow: "auto",
});

export const getStatusTabHeaderSx = (st: StatusTabTheme): SxProps<Theme> => ({
  px: 3,
  py: 2,
  borderBottom: `1px solid ${st.tableBorder}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: st.tableHeaderBarBg,
});

export const getStatusTabTitleSx = (st: StatusTabTheme): SxProps<Theme> => ({
  fontSize: 16,
  fontWeight: 600,
  color: st.tableTitleColor,
  fontFamily: interFont,
});

export const getStatusTableContainerSx = (
  st: StatusTabTheme,
): SxProps<Theme> => ({
  backgroundColor: st.tableSurface,
});

export const getStatusTableHeadRowSx = (
  st: StatusTabTheme,
): SxProps<Theme> => ({
  backgroundColor: st.tableHeadBg,
});

export const getStatusTableHeadCellSx = (
  st: StatusTabTheme,
  width: string,
  extra?: SxProps<Theme>,
): SxProps<Theme> => ({
  fontWeight: 600,
  color: st.tableHeaderColor,
  fontSize: 13,
  py: 2,
  borderRight: `1px solid ${st.tableBorder}`,
  width,
  ...(extra as object),
});

export const statusTableBodySx: SxProps<Theme> = {
  overflow: "scroll",
  minWidth: "1200px",
};

export const getStatusTableRowSx = (
  st: StatusTabTheme,
  rowBg: string,
): SxProps<Theme> => ({
  "&:hover": { backgroundColor: st.rowHoverBg },
  borderBottom: `1px solid ${st.rowBorder}`,
  backgroundColor: rowBg,
  cursor: "pointer",
});

export const getStatusTableCellSx = (
  st: StatusTabTheme,
  extra?: SxProps<Theme>,
): SxProps<Theme> => ({
  py: 2,
  fontSize: 13,
  color: st.tableCellColor,
  borderRight: `1px solid ${st.tableBorder}`,
  ...(extra as object),
});

export const statusSubStatusChipSx: SxProps<Theme> = {
  backgroundColor: "#FFEBE6",
  color: "#42526E",
  fontWeight: 600,
  fontSize: 12,
  borderRadius: "4px",
  border: "none",
  height: "auto",
  minHeight: "24px",
  "& .MuiChip-label": {
    whiteSpace: "normal",
    padding: "4px 8px",
  },
};

export const getInterviewResultChipSx = (bg: string): SxProps<Theme> => ({
  backgroundColor: bg,
  color: "#42526E",
  fontWeight: 600,
  fontSize: 12,
  borderRadius: "4px",
  border: "none",
  height: "auto",
  minHeight: "24px",
  "& .MuiChip-label": {
    whiteSpace: "normal",
    padding: "4px 8px",
  },
});

export const kvkkCellInnerSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  height: "100%",
};

export const getKvkkChipSx = (
  bg: string,
  notesColor: string,
): SxProps<Theme> => ({
  backgroundColor: bg,
  color: notesColor,
  fontWeight: 500,
  fontSize: 12,
  border: "none",
  borderRadius: "4px",
});

export const kvkkSendIconButtonSx: SxProps<Theme> = {
  color: "#5E6C84",
  "&:hover": {
    backgroundColor: colors.blue.B50,
  },
};

export const kvkkSendIconStyle: CSSProperties = {
  width: 18,
  height: 18,
};

export const kvkkEmptyPlaceholderSx: SxProps<Theme> = { height: 24 };

/**
 * Notlar hücresi HTML içerik render'i. `dangerouslySetInnerHTML` ile basılan Quill
 * çıktısının p/ul/ol/li kurallarını taşır. `maxHeight` tabloyu tek satır yüksekliğinde
 * tutar (upstream 09df543); liste stilleri 366a5d0 ile geldi.
 *
 * FORK SAPMASI: upstream `wordWrap: "break-word"` (yalnizca kelime sinirindan
 * kirar) kullaniyor. Notlar sutunu `tableLayout: "fixed"` icinde %14 genislikte
 * ve `overflow: hidden`; tek parca uzun bir jeton (URL, uzun Turkce bilesik,
 * yapistirilmis Guid) kelime siniri tasimadigi icin sarilmak yerine kirpiliyordu.
 * `wordBreak: "break-word"` + `overflowWrap: "anywhere"` jeton ortasindan kirar.
 */
export const getStatusNotesHtmlSx = (
  notesColor: string,
): SxProps<Theme> => ({
  fontSize: 13,
  color: notesColor,
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  lineHeight: 1.4,
  fontFamily: interFont,
  maxHeight: 96,
  overflow: "hidden",
  "& p": { m: 0, mb: "0.25em" },
  "& p:last-child": { mb: 0 },
  "& ul": {
    listStyle: "disc inside",
    m: 0,
    pl: "1.25em",
  },
  "& ol": {
    listStyle: "decimal inside",
    m: 0,
    pl: "1.25em",
  },
  "& li": {
    display: "list-item",
  },
  "& li > ul, & li > ol": {
    pl: "1.25em",
  },
});

/** Notlar hücresi düz metin render'i (HTML olmayan legacy kayıtlar). */
// FORK SAPMASI: yukaridaki getStatusNotesHtmlSx ile ayni gerekce.
export const getStatusNotesTextSx = (notesColor: string): SxProps<Theme> => ({
  fontSize: 13,
  color: notesColor,
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  lineHeight: 1.4,
  fontFamily: interFont,
});

export const statusActionCellSx: SxProps<Theme> = {
  py: 2,
  px: { xs: 0.5, sm: 1 },
  width: { xs: "44px", sm: "60px" },
  minWidth: { xs: "44px", sm: "60px" },
  maxWidth: { xs: "44px", sm: "60px" },
  textAlign: "center",
};

export const getStatusMenuButtonSx = (
  st: StatusTabTheme,
): SxProps<Theme> => ({
  width: { xs: 22, sm: 24 },
  height: { xs: 22, sm: 24 },
  borderRadius: "22px",
  backgroundColor: st.menuButtonBg,
  color: st.menuButtonColor,
  "&:hover": {
    backgroundColor: st.menuButtonHoverBg,
  },
});

export const getStatusMenuPaperSx = (st: StatusTabTheme): SxProps<Theme> => ({
  boxShadow:
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  border: `1px solid ${st.tableBorder}`,
  backgroundColor: st.tableSurface,
  color: st.textPrimary,
});

export const getStatusMenuItemSx = (enabled: boolean): SxProps<Theme> => ({
  opacity: enabled ? 1 : 0.5,
  cursor: enabled ? "pointer" : "not-allowed",
});

export const getStatusMenuDeleteItemSx = (
  enabled: boolean,
): SxProps<Theme> => ({
  color: "#DC2626",
  opacity: enabled ? 1 : 0.5,
  cursor: enabled ? "pointer" : "not-allowed",
});

export const statusMenuIconStyle: CSSProperties = {
  width: 18,
  height: 18,
  marginRight: 16,
  display: "block",
};

export const statusEmptyStateSx: SxProps<Theme> = {
  p: 6,
  textAlign: "center",
};

export const statusEmptyStateTextSx: SxProps<Theme> = {
  color: "#6B7280",
  mb: 2,
  fontFamily: interFont,
};

export const centeredSnackbarSx: SxProps<Theme> = {
  position: "fixed",
  left: "50% !important",
  top: "50% !important",
  transform: "translate(-50%, -50%) !important",
  "& .MuiPaper-root": { minWidth: 320 },
};

export const errorSnackbarSx: SxProps<Theme> = {
  ...centeredSnackbarSx,
  zIndex: (theme) => theme.zIndex.modal + 3,
};

export const fullWidthAlertSx: SxProps<Theme> = { width: "100%" };

// ===== StatusFormModal =====

export const fieldMinHeightSx: SxProps<Theme> = { minHeight: "80px" };

export const statusFormGridSx: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  columnGap: 3,
  rowGap: 1.5,
  mt: 1,
};

export const getStatusFormPaperSx = (
  darkModeActive: boolean,
  t: WiserThemeTokens,
): SxProps<Theme> => ({
  bgcolor: darkModeActive ? t.surface : "#FFFFFF",
  color: t.textPrimary,
});

export const getStatusFormTitleSx = (
  darkModeActive: boolean,
): SxProps<Theme> => ({
  ...getDialogTitleSx(darkModeActive, 1),
  m: 0,
  fontFamily: interFont,
  fontWeight: 600,
  fontSize: 16,
  color: darkModeActive ? "#FFFFFF" : "#253858",
});

export const getStatusFormCloseButtonSx = (
  darkModeActive: boolean,
): SxProps<Theme> => ({
  color: darkModeActive ? "#FFFFFF" : "#6B7280",
  "&:hover": {
    backgroundColor: darkModeActive ? "rgba(255,255,255,0.08)" : "#E5E7EB",
  },
});

export const getStatusFormContentSx = (
  darkModeActive: boolean,
  t: WiserThemeTokens,
): SxProps<Theme> => ({
  px: 3,
  zIndex: 9999,
  overflow: "auto",
  bgcolor: darkModeActive ? t.surface : undefined,
});

export const statusFormLoadingBoxSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: 400,
  flexDirection: "column",
};

export const getStatusFormLoadingTextSx = (
  darkModeActive: boolean,
): SxProps<Theme> => ({
  color: darkModeActive ? "#FFFFFF" : "#6B7280",
  fontFamily: "Inter, sans-serif",
  fontSize: 14,
});

export const dateTimeRowSx: SxProps<Theme> = {
  display: "flex",
  gap: 1.5,
  alignItems: "flex-start",
};

export const flexGrowMinWidthSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const fieldErrorCaptionSx: SxProps<Theme> = {
  display: "block",
  mt: 0.5,
  minHeight: "20px",
  fontFamily: "Inter, sans-serif",
};

export const autocompleteOptionStyle: CSSProperties = {
  fontFamily: "Inter, sans-serif",
};

export const getRejectionOptionStyle = (
  grouped: boolean,
  darkModeActive: boolean,
): CSSProperties => ({
  fontFamily: "Inter, sans-serif",
  color: grouped ? undefined : darkModeActive ? "#FFFFFF" : "#374151",
  fontWeight: grouped ? undefined : 600,
});

export const getRejectionGroupHeaderSx = (
  darkModeActive: boolean,
  t: WiserThemeTokens,
): SxProps<Theme> => ({
  px: 2,
  py: 1,
  fontFamily: "Inter, sans-serif",
  fontWeight: 600,
  color: darkModeActive ? "#FFFFFF" : "#374151",
  backgroundColor: darkModeActive ? t.surfaceMuted : "#fff",
  position: "sticky",
  top: -8,
  zIndex: 1,
});

export const getRejectionGroupListSx = (indented: boolean): SxProps<Theme> => ({
  p: 0,
  m: 0,
  listStyle: "none",
  pl: indented ? 2 : 0,
});

export const cvDetailTextSx: SxProps<Theme> = {
  fontFamily: "Inter, sans-serif",
  fontSize: 14,
};

export const cvUploadButtonSx: SxProps<Theme> = {
  textTransform: "none",
  fontFamily: "Inter, sans-serif",
  mb: 0.5,
};

export const cvErrorCaptionSx: SxProps<Theme> = {
  display: "block",
  color: "#d32f2f",
};

export const cvHintCaptionSx: SxProps<Theme> = {
  display: "block",
  color: "#6B7280",
};

// ===== StatusFormModal — Notlar HTML editörü (upstream 09df543) =====
export const notesEditorWrapSx: SxProps<Theme> = {
  minHeight: "80px",
  pb: 8,
};

export const notesEditorHeaderSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 1.5,
  mb: 1,
};

export const getNotesSourceButtonStyle = (
  showSource: boolean,
  darkModeActive: boolean,
  t: WiserThemeTokens,
): CSSProperties => ({
  padding: "6px 18px",
  borderRadius: "6px",
  background: showSource
    ? darkModeActive
      ? t.accent
      : "#374E7D"
    : darkModeActive
      ? t.inputBg
      : "#F3F4F6",
  color: showSource ? "#fff" : darkModeActive ? t.textPrimary : "#374E7D",
  fontWeight: 500,
  fontSize: "15px",
  boxShadow: showSource ? "0 2px 8px rgba(55,78,125,0.08)" : "none",
  cursor: "pointer",
  transition: "all 0.2s",
  border: showSource
    ? `1px solid ${darkModeActive ? t.accent : "#374E7D"}`
    : `1px solid ${darkModeActive ? t.borderStrong : "#E5E7EB"}`,
});

export const getNotesSourceBoxSx = (
  hasError: boolean,
  darkModeActive: boolean,
  t: WiserThemeTokens,
): SxProps<Theme> => ({
  background: darkModeActive ? t.inputBg : "#F9FAFB",
  border: `1.5px solid ${
    hasError ? "#d32f2f" : darkModeActive ? t.border : "#E5E7EB"
  }`,
  borderRadius: "8px",
  boxShadow: darkModeActive ? "none" : "0 2px 8px rgba(55,78,125,0.04)",
  padding: "16px",
  minHeight: 220,
  display: "flex",
  flexDirection: "column",
});

export const getNotesSourceTextareaStyle = (
  darkModeActive: boolean,
  t: WiserThemeTokens,
): CSSProperties => ({
  width: "100%",
  height: 180,
  fontFamily: "Fira Mono, monospace",
  fontSize: "15px",
  border: "none",
  background: "transparent",
  resize: "vertical",
  outline: "none",
  color: darkModeActive ? t.textPrimary : "#374E7D",
});

export const getNotesQuillWrapperSx = (
  hasError: boolean,
  darkModeActive: boolean,
  t: WiserThemeTokens,
): SxProps<Theme> => {
  const borderColor = hasError
    ? "#d32f2f"
    : darkModeActive
      ? t.border
      : "#E5E7EB";
  return {
    "& .ql-toolbar.ql-snow": {
      borderColor,
      backgroundColor: darkModeActive ? t.inputBg : undefined,
      borderTopLeftRadius: "8px",
      borderTopRightRadius: "8px",
    },
    "& .ql-container.ql-snow": {
      borderColor,
      backgroundColor: darkModeActive ? t.inputBg : undefined,
      color: darkModeActive ? t.textPrimary : undefined,
      borderBottomLeftRadius: "8px",
      borderBottomRightRadius: "8px",
    },
    "& .ql-editor": {
      color: darkModeActive ? t.textPrimary : undefined,
    },
    "& .ql-editor.ql-blank::before": {
      color: darkModeActive ? t.textMuted : undefined,
    },
    "& .ql-snow .ql-stroke": {
      stroke: darkModeActive ? t.textSecondary : undefined,
    },
    "& .ql-snow .ql-fill": {
      fill: darkModeActive ? t.textSecondary : undefined,
    },
    "& .ql-snow .ql-picker": {
      color: darkModeActive ? t.textPrimary : undefined,
    },
    "& .ql-snow .ql-picker-options": {
      backgroundColor: darkModeActive ? t.surface : undefined,
      borderColor: darkModeActive ? t.border : undefined,
    },
  };
};

export const notesQuillStyle: CSSProperties = {
  height: 220,
  marginBottom: 16,
  borderRadius: 8,
  boxShadow: "0 2px 8px rgba(55,78,125,0.04)",
};

export const notesErrorTextSx: SxProps<Theme> = { mt: 1 };

export const getStatusFormActionsSx = (
  darkModeActive: boolean,
  isDetail: boolean,
): SxProps<Theme> => ({
  ...getDialogActionsSx(darkModeActive),
  pb: 1,
  pt: 1,
  gap: 0.5,
  display: "flex",
  justifyContent: isDetail ? "flex-end" : "space-between",
  alignItems: "center",
});

export const getStatusFormClearButtonSx = (
  darkModeActive: boolean,
  t: WiserThemeTokens,
): SxProps<Theme> => ({
  color: darkModeActive ? t.link : "#2563EB",
  textTransform: "none",
  fontFamily: interFont,
});

export const getStatusFormSaveButtonSx = (darkModeActive: boolean) =>
  withDarkModalSaveButton(darkModeActive, {
    ...lightNavyButtonSx,
    fontFamily: interFont,
    px: 2,
    py: 1,
    borderRadius: 1,
  });

export const footerButtonsRowSx: SxProps<Theme> = {
  display: "flex",
  gap: 1,
};

export const experienceRowSx: SxProps<Theme> = {
  display: "flex",
  gap: 2,
};

export const getAccentProgressSx = (
  t: WiserThemeTokens,
): SxProps<Theme> => ({
  color: t.accent,
});

export const flexOneSx: SxProps<Theme> = { flex: 1 };

export const fieldAdornmentProgressSx: SxProps<Theme> = { mr: 1 };
